import { useTranslations } from '@vocab/react';
import { Alert, Text } from 'braid-design-system';
import React, { type ComponentProps } from 'react';
import { SlideDown } from 'react-slidedown';

import { TextLink } from 'src/components';
import { TRACKING_CODES } from 'src/shared/constant';
import { useConfig } from 'src/shared/hooks';
import { siteNameByBrand } from 'src/utils/helpers';

import type { SuggestionByCountry } from '../../helpers/suggestionMapping';
import translations from '../SubjectData/.vocab';

interface Props {
  tone?: ComponentProps<typeof Alert>['tone'];
  suggestion: SuggestionByCountry;
}

const ArticleSuggest = ({ suggestion, tone = 'info' }: Props) => {
  const { t } = useTranslations(translations);
  const { brand } = useConfig();

  const formatPlaceholderText = (text: string): string =>
    text
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const generateMessage = (
    suggestionObj: SuggestionByCountry,
  ): React.ReactNode[] => {
    const { message, urls } = suggestionObj;

    // IMPORTANT
    // We need to translate the message before replacing any brand or placeholder, otherwise, it will not be able to find the right translations.
    // ToDO: to revisit how this can be handled better
    const translatedMessage =
      (t(message as any, { brandName: siteNameByBrand[brand] }) as string) ??
      message;

    const placeholderRegex = /(%\w+)/g;
    const segments = translatedMessage.split(placeholderRegex);

    // Use React.Children.toArray to handle fragments and ensure keys
    return React.Children.toArray(
      segments.map((segment: string) => {
        if (placeholderRegex.test(segment)) {
          const key = segment.replace('%', ''); // i.e., "UpdateNameAndPhone"
          const urlSuggestion = urls.find((item) => item.placeholder === key);
          if (urlSuggestion) {
            return (
              <TextLink
                target="_blank"
                tracking={TRACKING_CODES.can_help_centre_link_clicked}
                href={urlSuggestion.url}
                product="candidate"
              >
                {formatPlaceholderText(urlSuggestion.label ?? '')}
              </TextLink>
            );
          }
        }
        return segment;
      }),
    );
  };

  const message = generateMessage(suggestion);

  return (
    <SlideDown
      style={{
        height: 0,
        transitionProperty: 'none',
        transitionDuration: '0.3s',
        transitionTimingFunction: 'ease-in-out',
        overflowY: 'hidden',
      }}
    >
      <Alert tone={tone}>
        <Text>{message}</Text>
      </Alert>
    </SlideDown>
  );
};

export default ArticleSuggest;
